import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { emailSchema, fullNameSchema, type AttemptState, type ExamInfo } from 'shared';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { translateApiError } from '@/lib/api-error';
import { writeSessionToken } from '@/lib/exam-session';

type FieldErrors = { fullName?: string; email?: string; consent?: string };

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token = '' } = useParams();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot — ผู้ใช้จริงมองไม่เห็น
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const exam = useQuery({
    queryKey: ['exam', token],
    queryFn: () => apiFetch<ExamInfo>(`/public/exam/${encodeURIComponent(token)}`),
    retry: false,
  });

  const start = useMutation({
    mutationFn: () =>
      apiFetch<AttemptState>(`/public/exam/${encodeURIComponent(token)}/start`, {
        method: 'POST',
        body: {
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          ...(organization.trim() ? { organization: organization.trim() } : {}),
          consent,
          ...(website ? { website } : {}),
        },
      }),
    onSuccess: (state) => {
      if (state.sessionToken) writeSessionToken(state.sessionToken);
      queryClient.setQueryData(['attempt'], state);
      void navigate(`/t/${token}/exam`);
    },
  });

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!fullNameSchema.safeParse(fullName).success) next.fullName = t('register.errors.fullName');
    if (!emailSchema.safeParse(email).success) next.email = t('register.errors.email');
    if (!consent) next.consent = t('register.errors.consent');
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  // ห้ามใช้ form ที่ submit จริง — จัดการด้วย handler เอง
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!validate()) return;
    start.mutate();
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-5 px-5 py-8">
      {/* ชื่อหลักสูตรยาว ถ้าวางข้างปุ่มภาษาจะโดนบีบเหลือครึ่งจอบนมือถือ — แยกบรรทัดแบบหน้าคำชี้แจง */}
      <header className="flex items-start justify-between gap-4">
        <p className="text-sm font-medium text-slate-500">{t('app.org')}</p>
        <LanguageSwitcher />
      </header>
      <div>
        {exam.data?.courseTitle && (
          <p className="text-sm font-medium text-slate-500">{exam.data.courseTitle}</p>
        )}
        <h1 className="mt-1 text-xl font-semibold text-slate-900">{t('register.title')}</h1>
      </div>

      {exam.data?.kind === 'posttest' && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 font-medium text-amber-900">
          <AlertTriangle aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
          {t('landing.posttestWarning')}
        </p>
      )}

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-slate-700">
            {t('register.fullName')} <span aria-hidden className="text-red-600">*</span>
            <span className="sr-only">({t('register.required')})</span>
          </label>
          <input
            id="fullName"
            name="fullName"
            autoComplete="name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            aria-invalid={Boolean(fieldErrors.fullName)}
            aria-describedby={fieldErrors.fullName ? 'fullName-error' : undefined}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
          />
          {fieldErrors.fullName && (
            <p id="fullName-error" className="mt-1 text-sm text-red-700">
              {fieldErrors.fullName}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            {t('register.email')} <span aria-hidden className="text-red-600">*</span>
            <span className="sr-only">({t('register.required')})</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
          />
          {fieldErrors.email && (
            <p id="email-error" className="mt-1 text-sm text-red-700">
              {fieldErrors.email}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="organization" className="block text-sm font-medium text-slate-700">
            {t('register.organization')}{' '}
            <span className="font-normal text-slate-500">({t('register.optional')})</span>
          </label>
          <input
            id="organization"
            name="organization"
            autoComplete="organization"
            value={organization}
            onChange={(event) => setOrganization(event.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
          />
        </div>

        {/* honeypot กันบอท — ซ่อนจากผู้ใช้จริงและจาก screen reader */}
        <div className="hidden" aria-hidden>
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>

        <div>
          <label className="flex items-start gap-3 rounded-lg border border-slate-300 p-3.5">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              aria-describedby={fieldErrors.consent ? 'consent-error' : undefined}
              className="mt-1 h-5 w-5 shrink-0 accent-slate-900"
            />
            <span className="text-sm leading-relaxed text-slate-700">
              {t('register.consent')}{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline">
                {t('register.consentLink')}
              </a>
            </span>
          </label>
          {fieldErrors.consent && (
            <p id="consent-error" className="mt-1 text-sm text-red-700">
              {fieldErrors.consent}
            </p>
          )}
        </div>

        {start.isError && (
          <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            {translateApiError(start.error, t)}
          </p>
        )}

        <Button type="submit" size="lg" disabled={start.isPending}>
          {start.isPending ? t('register.submitting') : t('register.submit')}
        </Button>
      </form>
    </main>
  );
}
